import React, { useState, useEffect } from "react";
import Modal from "../../Modal";
import { Plus } from "lucide-react";
import SearchableSelect from "../../SearchableSelect";
import { formatNumberWithSeparator } from "../../../utils/numberFormat";
import { accountService } from "../../../api/accountService";
import { useNotification } from "../../../contexts/NotificationContext";

export default function ShiftAddMutationModal({ isOpen, onClose, onSuccess }) {
  const { showSuccess, showError } = useNotification();
  const [formData, setFormData] = useState({
    category: "keluar", // Default keluar atau masuk
    jenis: "BIAYA",
    nominal: "",
    accountId: "",
    keterangan: "",
    lampiran: null,
  });

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchCashierAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await accountService.list({ page: 1, limit: 100 });
        const rawAccounts = response?.data || [];
        // Filter hanya akun yang aktif dan switch 'Tampilkan ke Kasir' nya ON (show_to_cashier === true)
        const cashierAccounts = rawAccounts.filter(
          (acc) => acc.status === "ACTIVE" && (acc.show_to_cashier === true || acc.show_to_cashier === 1)
        );
        setAccounts(cashierAccounts);

        // Auto selected akun pertama jika belum ada yang terpilih
        if (cashierAccounts.length > 0) {
          setFormData((prev) => ({
            ...prev,
            accountId: prev.accountId || cashierAccounts[0].id,
          }));
        }
      } catch (err) {
        console.error("Gagal memuat akun kasir:", err);
        showError("Gagal memuat daftar akun keuangan kasir");
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchCashierAccounts();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        lampiran: file,
      }));
    }
  };

  const handleCategoryChange = (category) => {
    setFormData((prev) => ({
      ...prev,
      category,
      jenis: category === "masuk" ? "PENDAPATAN" : "BIAYA",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.accountId) {
      showError("Pilih akun keuangan terlebih dahulu");
      return;
    }

    const rawNominal = parseInt(String(formData.nominal).replace(/\./g, ""), 10);
    if (isNaN(rawNominal) || rawNominal <= 0) {
      showError("Nominal mutasi harus lebih besar dari 0");
      return;
    }

    try {
      setSubmitting(true);

      // Upload file lampiran jika ada
      let uploadedAttachment = null;
      if (formData.lampiran) {
        const uploadRes = await accountService.uploadProof(formData.lampiran, "account-mutations");
        const data = uploadRes?.data || uploadRes;
        uploadedAttachment = {
          url: data.url || data.path || data.file_url || "",
          filename: formData.lampiran.name,
          mime_type: formData.lampiran.type,
          size: formData.lampiran.size,
        };
      }

      const attachments = uploadedAttachment ? [uploadedAttachment] : [];
      const notes = formData.keterangan || "";
      const selectedKind = formData.jenis;

      if (formData.category === "masuk") {
        const depositPayload = {
          amount: rawNominal,
          kind: selectedKind,
          notes,
          reference_type: "MANUAL",
          reference_id: "",
          attachments,
        };

        if (uploadedAttachment) {
          depositPayload.proof_url = uploadedAttachment.url;
          depositPayload.proof_name = uploadedAttachment.filename;
          depositPayload.proof_type = uploadedAttachment.mime_type;
          depositPayload.proof_size = uploadedAttachment.size;
        }

        await accountService.deposit(formData.accountId, depositPayload);
        showSuccess("Uang masuk berhasil ditambahkan");
      } else {
        const withdrawPayload = {
          amount: rawNominal,
          kind: selectedKind,
          notes,
          reference_type: "MANUAL",
          reference_id: "",
          attachments,
        };

        if (uploadedAttachment) {
          withdrawPayload.proof_url = uploadedAttachment.url;
          withdrawPayload.proof_name = uploadedAttachment.filename;
          withdrawPayload.proof_type = uploadedAttachment.mime_type;
          withdrawPayload.proof_size = uploadedAttachment.size;
        }

        await accountService.withdraw(formData.accountId, withdrawPayload);
        showSuccess("Uang keluar berhasil ditambahkan");
      }

      // Reset form
      setFormData({
        category: "keluar",
        jenis: "BIAYA",
        nominal: "",
        accountId: accounts.length > 0 ? accounts[0].id : "",
        keterangan: "",
        lampiran: null,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Gagal menambahkan mutasi:", err);
      showError(err?.response?.data?.message || err?.message || "Gagal menambahkan mutasi");
    } finally {
      setSubmitting(false);
    }
  };

  const jenisOptions =
    formData.category === "masuk"
      ? [
          { value: "PENDAPATAN", label: "Pendapatan" },
          { value: "NON_PENDAPATAN", label: "Non-Pendapatan" },
        ]
      : [
          { value: "BIAYA", label: "Biaya" },
          { value: "NON_BIAYA", label: "Non-Biaya" },
        ];

  const accountLabelText =
    formData.category === "keluar" ? "Diambil Dari Akun" : "Disimpan Ke Akun";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tambah Mutasi Kasir"
      headerColor="bg-teal-600">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Kategori <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={[
                { value: "masuk", label: "Uang Masuk" },
                { value: "keluar", label: "Uang Keluar" },
              ]}
              value={formData.category}
              onChange={handleCategoryChange}
              placeholder="Pilih Kategori"
              displayKey="label"
              valueKey="value"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Jenis <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={jenisOptions}
              value={formData.jenis}
              onChange={(value) =>
                handleChange({ target: { name: "jenis", value } })
              }
              placeholder="Pilih Jenis"
              displayKey="label"
              valueKey="value"
            />
          </div>
        </div>

        {/* Nominal */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Nominal <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium text-sm">
              Rp
            </span>
            <input
              type="text"
              name="nominal"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatNumberWithSeparator(formData.nominal)}
              onChange={(e) => {
                const rawValue = e.target.value.replace(/\./g, "");
                setFormData((prev) => ({ ...prev, nominal: rawValue }));
              }}
              placeholder="0"
              className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
              required
            />
          </div>
        </div>

        {/* Pilih Akun Keuangan (Diambil Dari Akun / Disimpan Ke Akun) */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            {accountLabelText} <span className="text-rose-500">*</span>
          </label>
          {loadingAccounts ? (
            <div className="text-xs text-slate-400 py-2">Memuat daftar akun kasir...</div>
          ) : accounts.length === 0 ? (
            <div className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-lg border border-amber-200">
              Tidak ada akun keuangan yang aktif dengan opsi 'Tampilkan Ke Kasir'.
            </div>
          ) : (
            <SearchableSelect
              options={accounts}
              value={formData.accountId}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, accountId: value }))
              }
              placeholder="Pilih Akun Keuangan"
              displayKey="name"
              valueKey="id"
            />
          )}
        </div>

        {/* Keterangan */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Keterangan
          </label>
          <textarea
            name="keterangan"
            value={formData.keterangan}
            onChange={handleChange}
            placeholder="Masukan keterangan disini..."
            rows="3"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors resize-none"
          />
        </div>

        {/* Lampiran */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Lampiran
          </label>
          <div className="border-2 border-dashed border-slate-200 bg-slate-50/40 rounded-xl p-4 text-center hover:border-teal-400 hover:bg-teal-50/40 transition-colors">
            <input
              type="file"
              id="shift-mutation-file-upload"
              onChange={handleFileChange}
              accept=".png,.jpg,.jpeg,.pdf"
              className="hidden"
            />
            <label htmlFor="shift-mutation-file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center">
                <Plus className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                <p className="text-xs text-slate-600">
                  <span className="text-teal-600 font-semibold">Upload file</span> atau drag and drop
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">PNG, JPG, PDF (Maks 10MB)</p>
              </div>
            </label>
            {formData.lampiran && (
              <p className="text-xs text-slate-700 mt-2 font-medium">
                Terpilih: {formData.lampiran.name}
              </p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={submitting || accounts.length === 0}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm shadow-sm shadow-teal-600/20 disabled:opacity-60">
            {submitting ? "Menyimpan..." : "Simpan Data"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
